import React, { useState, useEffect } from 'react';
import { Wind, AlertCircle } from 'lucide-react';
import axios from 'axios';

const AirQualityWidget = () => {
 const [aqiData, setAqiData] = useState(null);
 const [loading, setLoading] = useState(true);

 // Helper function to convert Celsius to Fahrenheit
 const convertCelsiusToFahrenheit = (celsius) => {
   return Math.round((celsius * 9/5) + 32);
 };

 // Get AQI level info
 const getAQILevel = (aqi) => {
   if (aqi <= 50) return { level: 'Good', color: 'text-green-600 bg-green-50' };
   if (aqi <= 100) return { level: 'Moderate', color: 'text-yellow-600 bg-yellow-50' };
   if (aqi <= 150) return { level: 'Unhealthy for Sensitive Groups', color: 'text-orange-600 bg-orange-50' };
   if (aqi <= 200) return { level: 'Unhealthy', color: 'text-red-600 bg-red-50' };
   if (aqi <= 300) return { level: 'Very Unhealthy', color: 'text-purple-600 bg-purple-50' };
   return { level: 'Hazardous', color: 'text-gray-600 bg-gray-50' };
 };

 useEffect(() => {
   const fetchAQI = async () => {
     try {
       const response = await axios.get(
         'https://api.waqi.info/feed/san-diego/?token=68314c1f051b52f6d4d2215ffc052b4d57d559a9'
       );
       setAqiData(response.data.data);
       setLoading(false);
     } catch (error) {
       console.error('Error fetching AQI data:', error);
       setLoading(false);
     }
   };

   fetchAQI();
 }, []);

 // Format the ISO date string
 const formatDateTime = (isoString) => {
   return new Date(isoString).toLocaleString('en-US', {
     month: 'short',
     day: 'numeric',
     year: 'numeric',
     hour: '2-digit',
     minute: '2-digit',
     timeZoneName: 'short'
   });
 };

 return (
   <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100 mt-6">
     <div className="flex items-center gap-2 mb-4">
       <Wind className="h-5 w-5 text-blue-600" />
       <h3 className="text-lg font-semibold text-gray-900">Air Quality Index</h3>
     </div>
     
     {loading ? (
       <div className="animate-pulse space-y-3">
         <div className="h-8 bg-gray-100 rounded w-1/2"></div>
         <div className="h-6 bg-gray-100 rounded w-3/4"></div>
       </div>
     ) : aqiData ? (
       <div className="space-y-4">
         <div className="flex items-center justify-between">
           <div>
             <span className="text-3xl font-bold text-gray-900">{aqiData.aqi}</span>
             <span className={`ml-3 px-3 py-1 rounded-full text-sm ${getAQILevel(aqiData.aqi).color}`}>
               {getAQILevel(aqiData.aqi).level}
             </span>
           </div>
           <div className="text-sm text-gray-500">
             Updated: {formatDateTime(aqiData.time.iso)}
           </div>
         </div>
         
         <div className="grid grid-cols-2 gap-4 mt-4">
           <div className="bg-gray-50 p-3 rounded">
             <div className="text-sm text-gray-500">PM2.5</div>
             <div className="text-lg font-semibold">
               {aqiData.iaqi.pm25?.v || 'N/A'} µg/m³
             </div>
           </div>
           <div className="bg-gray-50 p-3 rounded">
             <div className="text-sm text-gray-500">Temperature</div>
             <div className="text-lg font-semibold">
               {aqiData.iaqi.t?.v ? `${convertCelsiusToFahrenheit(aqiData.iaqi.t.v)}°F` : 'N/A'}
             </div>
           </div>
         </div>

         <div className="text-sm text-gray-500 mt-4">
           <AlertCircle size={16} className="inline mr-2" />
           Air quality can significantly impact outdoor living conditions
         </div>
       </div>
     ) : (
       <div className="text-gray-500">Unable to load air quality data</div>
     )}
   </div>
 );
};

export default AirQualityWidget;